import React, { useMemo, useState, useEffect } from 'react';

import { Field, FormikProps, useFormikContext } from 'formik';
import { ThemeProvider } from 'styled-components';

import {
  DateInputWrapper,
  DateInputValidationLabel,
  DateInputContainer,
  DateInputDropdown,
  DateInputPlaceholder,
} from './styled';

export interface DateInputProps extends FormikProps<any> {
  name: string;
  placeholder?: string;
  required?: boolean;
  yearRange?: { start: number; end: number };
  disabled?: boolean;
}

// The component will store a UTC Date object in the form field when both month and year are selected
// The Date object represents the first day of the selected month/year in UTC

const MONTHS = [
  { value: '', label: 'Month' },
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

export default function DateInput({
  name,
  placeholder,
  required,
  yearRange = { start: 1900, end: new Date().getFullYear() + 10 },
  disabled = false,
}: DateInputProps) {
  const formikContext = useFormikContext();

  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const isError = useMemo(() => {
    return formikContext.errors[name] && formikContext.touched[name];
  }, [formikContext.errors, formikContext.touched, name]);

  useEffect(() => {
    const formValue = formikContext.values[name];

    if (formValue instanceof Date) {
      const month = (formValue.getUTCMonth() + 1).toString().padStart(2, '0');
      const year = formValue.getUTCFullYear().toString();
      setSelectedMonth(month);
      setSelectedYear(year);
    } else if (formValue && typeof formValue === 'object') {
      setSelectedMonth(formValue.month || '');
      setSelectedYear(formValue.year || '');
    } else if (formValue && typeof formValue === 'string') {
      const [year, month] = formValue.split('-');
      setSelectedMonth(month);
      setSelectedYear(year);
    } else {
      setSelectedMonth('');
      setSelectedYear('');
    }
  }, [formikContext.values[name], name]);

  const createUTCDate = (month: string, year: string) => {
    if (!month || !year) return null;

    // Create UTC date for the first day of the selected month/year
    const utcDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, 1));
    utcDate.setUTCDate(2);
    return utcDate;
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);

    // Create UTC date if both month and year are selected
    if (newMonth && selectedYear) {
      const utcDate = createUTCDate(newMonth, selectedYear);
      if (utcDate) {
        formikContext.setFieldTouched(name, true, false);
        formikContext.setFieldValue(name, utcDate);
      }
    } else {
      // Clear the form value if not both are selected
      formikContext.setFieldValue(name, null);
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);

    // Create UTC date if both month and year are selected
    if (selectedMonth && newYear) {
      const utcDate = createUTCDate(selectedMonth, newYear);
      if (utcDate) {
        formikContext.setFieldTouched(name, true, false);
        formikContext.setFieldValue(name, utcDate.toISOString());
      }
    } else {
      // Clear the form value if not both are selected
      formikContext.setFieldValue(name, null);
    }
  };

  const generateYearOptions = () => {
    const years = [];
    for (let year = yearRange.end; year >= yearRange.start; year--) {
      years.push({ value: year.toString(), label: year.toString() });
    }
    return years;
  };

  const yearOptions = generateYearOptions();

  return (
    <ThemeProvider theme={{ isError }}>
      <DateInputWrapper>
        <input type="hidden" name={name} value={formikContext.values[name]} />
        {required !== undefined ? (
          required ? (
            <DateInputValidationLabel>
              {isError ? formikContext.errors[name] : 'Required'}
            </DateInputValidationLabel>
          ) : (
            <DateInputValidationLabel>Optional</DateInputValidationLabel>
          )
        ) : null}
        {formikContext.errors[name] && (
          <DateInputValidationLabel>{formikContext.errors[name]}</DateInputValidationLabel>
        )}
        <DateInputContainer>
          <DateInputDropdown value={selectedMonth} onChange={handleMonthChange} disabled={disabled}>
            {MONTHS.map((month) => (
              <option key={month.value} value={month.value} disabled={month.value === ''}>
                {month.label}
              </option>
            ))}
          </DateInputDropdown>
          <DateInputDropdown value={selectedYear} onChange={handleYearChange} disabled={disabled}>
            <DateInputPlaceholder value="" disabled>
              Year
            </DateInputPlaceholder>
            {yearOptions.map((year) => (
              <option key={year.value} value={year.value}>
                {year.label}
              </option>
            ))}
          </DateInputDropdown>
        </DateInputContainer>
      </DateInputWrapper>
    </ThemeProvider>
  );
}
